/*
 * Copyright (c) 2018 Honeywell International Inc. All Rights Reserved.
 * Honeywell and its product names are among the trademarks and/or service marks owned by Honeywell International, Inc., or its subsidiaries.
 *
 * For patent information, see http://www.hsmpats.com.
 */

import React, { Component } from 'react';
import {
  Grid,
  Search,
  Loader,
  Modal,
  Input,
  Button,
  Icon,
  Tooltip,
  InputLabel,
  Checkbox,
} from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { util, NotificationHeaderModal } from '@keystone/common';
import {
  withUserContext,
  withCustomerSiteContext,
  withApplicationContext,
} from '../../common/HOCs';
import AppModal from '../../partials/AppModal';
import SiteTable from '../../partials/SiteTable';
import APIClient from '../../common/APIClient';

class ViewUsers extends Component {
  constructor(props) {
    super(props);
    this.state = {
      form: {
        userName: null,
        firstName: null,
        lastName: null,
        phoneNumber: null,
        forceChangePassword: false,
        password: null,
        matchingPassword: null,
        admin: false,
        locked: false,
        sites: null,
      },
      users: null,
      loadingSettings: false,
      open: false,
      openEdit: false,
      disableMultipleDelete: true,
      hasDimmer: true,
      allSites: null,
      deleteInProgress: false,
    };
  }

  componentDidMount() {
    const { appContext } = this.props;
    appContext.registerReloadFunction(this.reloadTableComponent);
    this.initializeTable();

    if (this.props.appContext.notificationModal.notificationText.trim() === '') {
      this.props.appContext.setNotification(null);
    }
  }

  reloadTableComponent = async (signal) => {
    const { appContext, custSiteContext } = this.props;
    try {
      const users = await APIClient.fetchUsers(
        appContext.user,
        custSiteContext.selectedCustomer,
        custSiteContext.selectedSite,
        signal
      );
      this.setState({ users: users.response });
    } catch (err) {
      if (err.name !== 'AbortError') {
        appContext.handleError(err);
        console.error(err);
      }
    }
  };

  componentWillUnmount() {
    const { appContext } = this.props;
    appContext.unregisterReloadFunction();
  }

  initializeTable = async () => {
    this.setState({ loadingSettings: true });
    await this.reloadTableComponent();
    this.setState({ loadingSettings: false });
  };

  onFirstNameChange = (firstName) => {
    this.setState({ form: { ...this.state.form, firstName } });
  };

  onLastNameChange = (lastName) => {
    this.setState({ form: { ...this.state.form, lastName } });
  };

  onUserNameChange = (email) => {
    this.setState({ form: { ...this.state.form, email } });
  };

  onPasswordChange = (password) => {
    this.setState({ form: { ...this.state.form, password } });
  };

  onConfirmPasswordChange = (matchingPassword) => {
    this.setState({ form: { ...this.state.form, matchingPassword } });
  };

  onForceChangePasswordChange = (forceChangePassword) => {
    this.setState({ form: { ...this.state.form, forceChangePassword } });
  };

  onAdminUserChange = (admin) => {
    this.setState({ form: { ...this.state.form, admin } });
  };

  onAccountActiveChange = (locked) => {
    this.setState({ form: { ...this.state.form, locked } });
  };

  onPhoneChange = (phoneNumber) => {
    if (phoneNumber.trim() === '') {
      phoneNumber = null;
      this.setState({ form: { ...this.state.form, phoneNumber } });
    } else {
      this.setState({ form: { ...this.state.form, phoneNumber } });
    }
  };

 onEditUser = (props) => {
    const form = props;
    this.setState({ form }, function() {
      this.getSiteList(true, form.sites);
    });
    this.props.appContext.notificationModal.hasNotification = false;
    this.props.appContext.notificationModal.notificationText = '';
    this.props.appContext.notificationModal.severity = '';
    this.setState({ openEdit: true });
  };

  userActionIcons = (props) => {
    const { appContext } = this.props;
    if (util.getSentienceEnableFlag() || (!props.support && appContext.isAdmin)) {
      return (
        <>
          <a onClick={() => this.onEditUser(props)}>
            <Icon name="edit" size="large" />
          </a>
          <a onClick={() => this.handleSingleDeleteModal(props)}>
            <Icon name="delete" size="large" />
          </a>
        </>
      );
    }
    return <></>;
  };

  toggleMultipleDelete = (e) => {
    if (e.length > 1) {
      this.setState({ disableMultipleDelete: false });
    } else {
      this.setState({ disableMultipleDelete: true });
    }
  };

  disableMultipleDelete = () => {
    this.props.userContext.toggleConfirmDeleteUserModal();
    this.props.userContext.clearSelectedUsers();
    this.setState({ disableMultipleDelete: true });
  };

  handleMultipleDeleteButton = async () => {
    const { appContext, userContext } = this.props;
    this.setState({ deleteInProgress: true });
    await userContext.deleteUsers();
    await appContext.reload();
    this.setState({ disableMultipleDelete: true, deleteInProgress: false });
  };

  handleSingleDeleteModal = (props) => {
    this.props.userContext.setSelectedUser(props);
    this.props.userContext.toggleConfirmSingleDeleteUserModal();
  };

  clearSelectionAfterCancelEdit = () => {
    const form = {
      userName: null,
      firstName: null,
      lastName: null,
      phoneNumber: null,
      forceChangePassword: false,
      password: null,
      matchingPassword: null,
      admin: false,
    };
    this.setState({ openEdit: false });
    this.setState({ open: false });
    this.setState({ form });
    this.props.userContext.clearSelectedUsers();
    this.props.appContext.notificationModal.hasNotification = false;
    this.props.appContext.notificationModal.notificationText = '';
    this.props.appContext.notificationModal.severity = '';
  };

  openAddUserModal = () => {
    const form = {
      userName: null,
      firstName: null,
      lastName: null,
      phoneNumber: null,
      forceChangePassword: true,
      password: null,
      matchingPassword: null,
      admin: false,
    };
    this.setState({ form }, function() {
      this.getSiteList(false, form.sites);
    });

    this.props.appContext.notificationModal.hasNotification = false;
    this.props.appContext.notificationModal.notificationText = '';
    this.props.appContext.notificationModal.severity = '';
    this.setState({ open: true });
  };

  // function gets all the available sites in the customer
  getSiteList = async (isEditUser, associatedSites) => {
    const { selectedCustomer } = this.props.custSiteContext;
    let allSites = this.props.custSiteContext.siteList;

    // remove default site from the list
    allSites = allSites.filter((_site) => {
      return _site.value != '-1';
    });

    if (isEditUser) {
      associatedSites.map(function (site, index) {
        allSites = allSites.filter((_site) => {
          return _site.value != site.identifier;
        });
      });

      const sites = associatedSites.filter((_site) => {
        return _site.customer.customerId == selectedCustomer;
      });

      this.setState({ form: { ...this.state.form, sites } });
    } else {
      const sites = [];
      sites.push({ identifier: -1, siteName: 'Default' });
      // add site in associated list
      this.setState({ form: { ...this.state.form, sites } });
    }
    this.setState({ allSites });
  };

  setAllSites = (allSites) => {
    this.setState({ allSites });
  };

  setSites = (sites) => {
    this.setState({ form: { ...this.state.form, sites } });
  };

  renderActionButtons = () => {
    const { appContext } = this.props;
    if (util.getSentienceEnableFlag() || appContext.isAdmin) {
      return (
        <DataTable.HeaderBar>
          <DataTable.HeaderBar.Item
            content="Add"
            icon={<Icon name="slidercontrols-plus" size="small" root="common" />}
            onClick={() => this.openAddUserModal()}
          />
          <DataTable.HeaderBar.Item
            content="Delete"
            icon={<Icon name="user-remove" size="small" root="common" />}
            disabled={this.state.disableMultipleDelete}
            onClick={() => this.props.userContext.toggleConfirmDeleteUserModal()}
          />
          <DataTable.HeaderBar.Item content="" />
        </DataTable.HeaderBar>
      );
    }
    return <></>;
  };

  addUserSubmit = async () => {
    const { appContext, userContext } = this.props;
    const { form } = this.state;
    this.setState({ loadingSettings: true });
    const response = await userContext.createUser(form);
    await appContext.reload();
    this.setState({ loadingSettings: false });
    if (!(response instanceof Error)) {
      this.setState({ open: false });
    }
  };

  editUserSubmit = async () => {
    const { appContext, userContext } = this.props;
    const { form } = this.state;
    this.setState({ loadingSettings: true });
    const response = await userContext.editUser(form);
    await appContext.reload();
    this.setState({ loadingSettings: false });
    if (!(response instanceof Error)) {
      this.setState({ openEdit: false });
    }
  };

  render() {
    return (
      <div style={{ position: 'relative', height: '100%' }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={12}>
              <h3 />
              {this.renderModal()}
              {this.renderEditModal()}
              {this.renderDeleteModal()}
              {this.renderSingleDeleteModal()}
              <Loader
                loading={this.state.loadingSettings}
                overlayOpacity={0.2}
                overlayColor="#B2AFAF"
              >
                <div className="placeholder" />
              </Loader>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={12}>
              <DataTable
                key={this.props.custSiteContext.selectedCustomer}
                data={
                  this.state.users === null
                    ? []
                    : this.state.users === undefined
                    ? []
                    : this.state.users
                }
                resizableColumns
                columnResizeMode="fit"
                reorderableColumns={false}
                selection={this.props.userContext.selectedRows}
                onSelectionChange={(e) => {
                  this.props.userContext.setSelectedRows(e);
                  this.toggleMultipleDelete(e);
                }}
                scrollable
                search
                metaKeySelection={false}
              >
                {this.renderActionButtons()}
                <DataTable.Column selectionMode="multiple" initialWidth="5%" />
                <DataTable.Column field="email" header="Username" sortable initialWidth="25%" />
                <DataTable.Column
                  field="firstName"
                  header="First Name"
                  sortable
                  initialWidth="25%"
                />
                <DataTable.Column field="lastName" header="Last Name" sortable initialWidth="25%" />
                <DataTable.Column
                  field="lastName"
                  header="Actions"
                  sortable={false}
                  initialWidth="10%"
                  renderer={(props) => this.userActionIcons(props.rowData)}
                />
                <DataTable.Pagination
                  itemsPerPage={10}
                  totalItems={
                    this.state.users === null
                      ? 1
                      : this.state.users === undefined
                      ? 1
                      : this.state.users.length
                  }
                  showDisplayDetails
                />
              </DataTable>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    );
  }

  renderModal() {
    return (
      <Modal
        onClose={() => this.setState({ open: false })}
        open={this.state.open}
        dimmer={this.state.hasDimmer}
        closeOnDimmerClick={false}
        closeOnDocumentClick={false}
        shouldCloseOnOverlayClick
        size="large"
      >
        <Modal.Content>
          {this.props.appContext.notificationModal.hasNotification ? (
            <NotificationHeaderModal context={this.props.appContext} />
          ) : (
            <></>
          )}
          <Grid>
            <Grid.Row>
              <Grid.Column width={12}>
                <h1>Create User</h1>
                <Loader
                  loading={this.state.loadingSettings}
                  overlayOpacity={0.2}
                  overlayColor="#B2AFAF"
                >
                  <div className="placeholder" />
                </Loader>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={6}>
                <Input
                  fluid
                  error={null}
                  label="First Name"
                  maxLength="30"
                  placeholder="First Name"
                  indicator="required"
                  onChange={(data) => this.onFirstNameChange(data)}
                />
              </Grid.Column>
              <Grid.Column width={6}>
                <Input
                  fluid
                  error={null}
                  label="Last Name"
                  maxLength="30"
                  placeholder="Last Name"
                  indicator="required"
                  onChange={(data) => this.onLastNameChange(data)}
                />
              </Grid.Column>
            </Grid.Row>

            {util.getSentienceEnableFlag() ? (
              <>
                <Grid.Row>
                  <Grid.Column width={6}>
                    <Input
                      fluid
                      error={null}
                      label="Email/User Name"
                      placeholder="Email/User Name"
                      indicator="required"
                      onChange={(data) => this.onUserNameChange(data)}
                    />
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Input
                      fluid
                      error={null}
                      label="Phone"
                      maxLength="30"
                      placeholder="Phone*"
                      onChange={(data) => this.onPhoneChange(data)}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={12}>
                    <b>Note*</b> Phone should be country code plus user phone number e.g.
                    1xxxxxxxxxx, 1 is country code and rest is user phone number
                  </Grid.Column>
                </Grid.Row>
              </>
            ) : (
              <>
                <Grid.Row>
                  <Grid.Column width={12}>
                    <Input
                      fluid
                      error={null}
                      label="Email/User Name"
                      placeholder="Email/User Name"
                      indicator="required"
                      onChange={(data) => this.onUserNameChange(data)}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={6}>
                    <Grid>
                      <Grid.Row>
                        <Grid.Column width={12}>
                          <InputLabel label="Password" indicator="required" />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={10}>
                          <Input
                            fluid
                            error={null}
                            maxLength="30"
                            placeholder="Password"
                            type="password"
                            onChange={(data) => this.onPasswordChange(data)}
                          />
                        </Grid.Column>
                        <Grid.Column width={2}>
                          <Tooltip
                            content="Length should be in between 8 to 30 characters, Minimum 1 character should be uppercase, Minimum 1 character should be number, Minimum 1 character should be special, No sequential 3 numbers like '123' or '234'."
                            element={<Icon name="badge-info" root="common" />}
                            position="right"
                            event="hover"
                            hoverable
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Grid>
                      <Grid.Row>
                        <Grid.Column width={12}>
                          <InputLabel label="Confirm Password" indicator="required" />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={12}>
                          <Input
                            fluid
                            error={null}
                            maxLength="30"
                            placeholder="Confirm Password"
                            type="password"
                            indicator="required"
                            onChange={(data) => this.onConfirmPasswordChange(data)}
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4}>
                    <Checkbox
                      label="Force Change Password"
                      toggle
                      defaultChecked
                      onChange={(data) => this.onForceChangePasswordChange(data)}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Checkbox
                      label="Admin User"
                      toggle
                      onChange={(data) => this.onAdminUserChange(data)}
                    />
                  </Grid.Column>
                </Grid.Row>
              </>
            )}
            <>
              <Grid.Row>
                <Grid.Column width={4}>
                  <InputLabel label="Associated Sites:" />
                </Grid.Column>

                <Grid.Column width={4}>
                  <InputLabel label="Available Sites:" />
                </Grid.Column>
              </Grid.Row>

              <SiteTable
                sites={
                  this.state.form.sites === null
                    ? []
                    : this.state.form.sites === undefined
                    ? []
                    : this.state.form.sites
                }
                allSites={
                  this.state.allSites === null
                    ? []
                    : this.state.allSites === undefined
                    ? []
                    : this.state.allSites
                }
                setAllSites={this.setAllSites}
                setSites={this.setSites}
              />
            </>
          </Grid>
        </Modal.Content>
        <Modal.Footer>
          <Button
            type="secondary"
            size="small"
            content="Cancel"
            onClick={() => {
              this.clearSelectionAfterCancelEdit();
            }}
          />
          <Button
            type="primary"
            content="Submit"
            size="small"
            onClick={() => {
              this.addUserSubmit();
            }}
          />
        </Modal.Footer>
      </Modal>
    );
  }

  renderEditModal() {
    return (
      <Modal
        onClose={() => this.setState({ openEdit: false })}
        open={this.state.openEdit}
        dimmer={this.state.hasDimmer}
        closeOnDimmerClick={false}
        closeOnDocumentClick={false}
        shouldCloseOnOverlayClick
      >
        <Modal.Content>
          {this.props.appContext.notificationModal.hasNotification ? (
            <NotificationHeaderModal context={this.props.appContext} />
          ) : (
            <></>
          )}
          <Grid>
            <Grid.Row>
              <Grid.Column width={12}>
                <h1>Edit User</h1>
                <Loader
                  loading={this.state.loadingSettings}
                  overlayOpacity={0.2}
                  overlayColor="#B2AFAF"
                >
                  <div className="placeholder" />
                </Loader>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={6}>
                <Input
                  fluid
                  error={null}
                  label="First Name"
                  maxLength="30"
                  indicator="required"
                  value={this.state.form.firstName}
                  onChange={(data) => this.onFirstNameChange(data)}
                />
              </Grid.Column>
              <Grid.Column width={6}>
                <Input
                  fluid
                  error={null}
                  label="Last Name"
                  maxLength="30"
                  indicator="required"
                  value={this.state.form.lastName}
                  onChange={(data) => this.onLastNameChange(data)}
                />
              </Grid.Column>
            </Grid.Row>

            {util.getSentienceEnableFlag() && !this.state.form.isSupport ? (
              <>
                <Grid.Row>
                  <Grid.Column width={6}>
                    <Input
                      fluid
                      error={null}
                      label="User Name/Email"
                      maxLength="30"
                      indicator="required"
                      value={this.state.form.email}
                      disabled="true"
                      onChange={(data) => this.onEmailChange(data)}
                    />
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Input
                      fluid
                      error={null}
                      label="Phone"
                      placeholder="Phone*"
                      value={this.state.form.phoneNumber}
                      onChange={(data) => this.onPhoneChange(data)}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={12}>
                    <b>Note*</b> Phone should be country code plus user phone number e.g.
                    1xxxxxxxxxx, 1 is country code and rest is user phone number
                  </Grid.Column>
                </Grid.Row>
              </>
            ) : (
              <>
                <Grid.Row>
                  <Grid.Column width={12}>
                    <Input
                      fluid
                      error={null}
                      label="User Name/Email"
                      maxLength="30"
                      indicator="required"
                      value={this.state.form.email}
                      disabled="true"
                      onChange={(data) => this.onEmailChange(data)}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={6}>
                    <Grid>
                      <Grid.Row>
                        <Grid.Column width={12}>
                          <InputLabel label="Password" indicator="required" />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={10}>
                          <Input
                            fluid
                            error={null}
                            maxLength="30"
                            placeholder="Password*"
                            value={this.state.form.password}
                            type="password"
                            onChange={(data) => this.onPasswordChange(data)}
                          />
                        </Grid.Column>
                        <Grid.Column width={2}>
                          <Tooltip
                            content="Length should be in between 8 to 30 characters, Minimum 1 character should be uppercase, Minimum 1 character should be number, Minimum 1 character should be special, No sequential 3 numbers like '123' or '234'."
                            element={<Icon name="badge-info" root="common" />}
                            position="right"
                            event="hover"
                            hoverable
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <Grid>
                      <Grid.Row>
                        <Grid.Column width={12}>
                          <InputLabel label="Confirm Password" indicator="required" />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={12}>
                          <Input
                            fluid
                            error={null}
                            maxLength="30"
                            value={this.state.form.matchingPassword}
                            placeholder="Confirm Password"
                            type="password"
                            onChange={(data) => this.onConfirmPasswordChange(data)}
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4}>
                    <Checkbox
                      label="Force Change Password"
                      toggle
                      onChange={(data) => this.onForceChangePasswordChange(data)}
                      checked={this.state.form.forceChangePassword}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Checkbox
                      label="Admin User"
                      toggle
                      onChange={(data) => this.onAdminUserChange(data)}
                      checked={this.state.form.admin}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Checkbox
                      label="Account Locked"
                      toggle
                      onChange={(data) => this.onAccountActiveChange(data)}
                      checked={this.state.form.locked}
                    />
                  </Grid.Column>
                </Grid.Row>
              </>
            )}
            <>
              <Grid.Row>
                <Grid.Column width={4}>
                  <InputLabel label="Associated Sites:" />
                </Grid.Column>

                <Grid.Column width={4}>
                  <InputLabel label="Available Sites:" />
                </Grid.Column>
              </Grid.Row>

              <SiteTable
                sites={
                  this.state.form.sites === null
                    ? []
                    : this.state.form.sites === undefined
                    ? []
                    : this.state.form.sites
                }
                allSites={
                  this.state.allSites === null
                    ? []
                    : this.state.allSites === undefined
                    ? []
                    : this.state.allSites
                }
                setAllSites={this.setAllSites}
                setSites={this.setSites}
              />
            </>
          </Grid>
        </Modal.Content>
        <Modal.Footer>
          <Button
            type="secondary"
            size="small"
            content="Cancel"
            onClick={() => {
              this.clearSelectionAfterCancelEdit();
            }}
          />
          <Button
            type="primary"
            content="Submit"
            size="small"
            onClick={() => {
              this.editUserSubmit();
            }}
          />
        </Modal.Footer>
      </Modal>
    );
  }

  renderDeleteModal() {
    const { userContext } = this.props;
    const { deleteInProgress } = this.state;
    return (
      <AppModal
        isOpen={userContext.isConfirmDeleteModalOpen}
        content="Would you like to delete the selected users?"
        handleSubmit={() => this.handleMultipleDeleteButton()}
        handleToggle={() => this.disableMultipleDelete()}
        disableSubmit={deleteInProgress}
      />
    );
  }

  renderSingleDeleteModal() {
    const { appContext, userContext } = this.props;
    const { deleteInProgress } = this.state;
    return (
      <AppModal
        isOpen={userContext.isConfirmSingleDeleteModalOpen}
        content="Would you like to delete the selected user?"
        handleSubmit={async () => {
          this.setState({ deleteInProgress: true });
          await userContext.deleteUser();
          await appContext.reload();
          this.setState({ deleteInProgress: false });
        }}
        handleToggle={() => userContext.toggleConfirmSingleDeleteUserModal()}
        disableSubmit={deleteInProgress}
      />
    );
  }
}

export default withApplicationContext(withCustomerSiteContext(withUserContext(ViewUsers)));
